import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Car, Pencil, Trash2 } from "lucide-react";
import { formatDateES } from "@/lib/utils-app";

const RESULT_LABEL = {
  apto: { text: "APTO", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  no_apto: { text: "NO APTO", cls: "bg-red-100 text-red-800 border-red-300" },
  no_presentado: { text: "No presentado", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  anulado_dgt: { text: "Anulado DGT", cls: "bg-neutral-100 text-neutral-600 border-neutral-300" },
  "": { text: "Pendiente", cls: "bg-sky-100 text-sky-800 border-sky-300" },
};

/** V1.79 · Historial de exámenes del alumno con asignación manual de tasa. */
export default function ExamHistoryPanel({ studentId, tasas = [], onChanged, compact = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/exam-history`);
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const assign = async (appointmentId, examRequestId) => {
    setSavingId(appointmentId);
    try {
      const { data } = await api.patch(`/appointments/${appointmentId}/tasa`, { exam_request_id: examRequestId });
      setRows(data.history || []);
      toast.success(examRequestId ? "Examen asignado a la tasa" : "Examen desasignado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId("");
    }
  };

  const setResult = async (row, value) => {
    const [result, justified] = value === "no_presentado_justificado"
      ? ["no_presentado", true]
      : [value, false];
    const extra = row.result ? "\n\nYa hay un resultado registrado: se pedirá el PIN maestro." : "";
    if (!window.confirm(`¿Guardar el resultado de este examen?${extra}`)) return;
    setSavingId(row.appointment_id);
    try {
      await api.post(`/appointments/${row.appointment_id}/exam-result`, { result, justified });
      await load();
      toast.success("Resultado actualizado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingId(""); }
  };

  const saveEdit = async () => {
    setSavingId(edit.appointment_id);
    try {
      await api.patch(`/appointments/${edit.appointment_id}`, { date: edit.date, time: edit.time });
      setEdit(null);
      await load();
      toast.success("Examen actualizado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingId(""); }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `¿Eliminar este examen del historial?\n\n${row.kind === "teorico" ? "Teórico" : "Práctico"} · ${row.date} ${row.time}\n\nSe pedirá el PIN maestro y se borrará también de la agenda.`,
    )) return;
    setSavingId(row.appointment_id);
    try {
      await api.delete(`/appointments/${row.appointment_id}`);
      await load();
      toast.success("Examen eliminado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingId(""); }
  };

  if (loading) {
    return (
      <div className="text-sm text-neutral-500 py-3 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando historial…
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="exam-history-panel">
      {rows.length === 0 && (
        <p className="text-[12px] text-neutral-400">Este alumno todavía no se ha presentado a ningún examen.</p>
      )}
      {rows.map((r) => {
        const res = RESULT_LABEL[r.result] || RESULT_LABEL[""];
        return (
          <div
            key={r.appointment_id}
            data-testid={`exam-history-row-${r.appointment_id}`}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-[12px]"
          >
            <span className="inline-flex items-center gap-1 font-semibold text-neutral-700">
              {r.kind === "teorico" ? <BookOpen className="h-3.5 w-3.5" /> : <Car className="h-3.5 w-3.5" />}
              {r.kind === "teorico" ? "Teórico" : "Práctico"}
            </span>
            <span className="font-mono text-neutral-800">
              Fecha {formatDateES(r.date)}{r.time ? ` · Hora ${r.time}` : ""}
            </span>
            <span className={"px-1.5 py-0.5 rounded border text-[10px] font-bold " + res.cls}>{res.text}</span>
            {r.consumed === 0 && r.result && (
              <span className="text-[10px] text-neutral-400">no consume</span>
            )}
            <button
              type="button" title="Cambiar fecha y hora"
              data-testid={`exam-history-edit-${r.appointment_id}`}
              onClick={() => setEdit({ appointment_id: r.appointment_id, date: r.date, time: r.time })}
              className="p-1 rounded hover:bg-neutral-100 text-neutral-500"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button" title="Eliminar este examen (pide PIN maestro)"
              data-testid={`exam-history-delete-${r.appointment_id}`}
              onClick={() => remove(r)}
              className="p-1 rounded hover:bg-red-50 text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <div className="ml-auto flex items-center gap-1.5">
              <select
                value={r.result === "no_presentado" && r.justified ? "no_presentado_justificado" : (r.result || "")}
                disabled={savingId === r.appointment_id}
                onChange={(e) => setResult(r, e.target.value)}
                data-testid={`exam-history-result-${r.appointment_id}`}
                className="h-7 rounded-md border border-neutral-200 px-1.5 text-[11px] font-semibold"
                title="Resultado del examen (afecta a la tasa y al expediente)"
              >
                <option value="">Pendiente</option>
                <option value="apto">APTO</option>
                <option value="no_apto">NO APTO</option>
                <option value="no_presentado">No presentado</option>
                <option value="no_presentado_justificado">No presentado (justif.)</option>
                <option value="anulado_dgt">Anulado DGT</option>
              </select>
              <span className="text-[10px] uppercase tracking-wider text-neutral-400">Tasa</span>
              <select
                value={r.exam_request_id || ""}
                disabled={savingId === r.appointment_id}
                onChange={(e) => assign(r.appointment_id, e.target.value)}
                data-testid={`exam-history-tasa-${r.appointment_id}`}
                className={
                  "h-7 rounded-md border px-1.5 text-[11px] font-mono " +
                  (r.exam_request_id ? "border-neutral-200" : "border-amber-400 bg-amber-50")
                }
              >
                <option value="">— sin asignar —</option>
                {tasas.map((t) => (
                  <option
                    key={t.id}
                    value={t.id}
                    disabled={t.state?.exhausted && t.id !== r.exam_request_id}
                  >
                    {t.tasas_number || "(sin nº)"}{t.state?.exhausted ? " · agotada" : ""}
                  </option>
                ))}
              </select>
            </div>
            {edit?.appointment_id === r.appointment_id && (
              <div className="w-full flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-1.5 mt-1">
                <input
                  type="date" value={edit.date}
                  onChange={(e) => setEdit((s) => ({ ...s, date: e.target.value }))}
                  data-testid={`exam-history-edit-date-${r.appointment_id}`}
                  className="h-7 rounded-md border border-neutral-300 px-1.5 text-[11px] font-mono"
                />
                <input
                  type="time" value={edit.time}
                  onChange={(e) => setEdit((s) => ({ ...s, time: e.target.value }))}
                  data-testid={`exam-history-edit-time-${r.appointment_id}`}
                  className="h-7 rounded-md border border-neutral-300 px-1.5 text-[11px] font-mono"
                />
                <button
                  type="button" onClick={saveEdit} disabled={savingId === r.appointment_id}
                  data-testid={`exam-history-edit-save-${r.appointment_id}`}
                  className="h-7 px-2 rounded-md bg-neutral-900 text-white text-[11px] font-semibold disabled:opacity-60"
                >
                  Guardar
                </button>
                <button
                  type="button" onClick={() => setEdit(null)}
                  className="h-7 px-2 rounded-md border border-neutral-300 text-[11px] font-semibold"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        );
      })}
      {!compact && rows.some((r) => !r.exam_request_id) && (
        <p className="text-[11px] text-amber-700 font-semibold">
          Hay exámenes sin tasa asignada: elige su tasa en el desplegable para que cuenten en el saldo.
        </p>
      )}
    </div>
  );
}
